import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/apiClient";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import LeadFormDialog from "@/components/leads/LeadFormDialog";
import LeadTable from "@/components/leads/LeadTable";
import LeadKanban from "@/components/leads/LeadKanban";
import LeadDetailsSheet from "@/components/leads/LeadDetailsSheet";
import { LEAD_SOURCES, LEAD_STATUSES } from "@/lib/leadConstants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  AreaChart, Area,
} from "recharts";
import {
  Plus, Search, Download, Trash2, Users, CalendarPlus, TrendingUp, Percent, Loader2, LayoutGrid, List,
} from "lucide-react";

const DEFAULT_FILTERS = { search: "", status: "all", source: "all", assigned_to: "all" };

function Kpi({ icon: Icon, label, value, tint, testid }) {
  return (
    <div data-testid={testid} className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">{label}</span>
        <span className={`flex h-8 w-8 items-center justify-center rounded-lg ${tint}`}><Icon className="h-4 w-4" /></span>
      </div>
      <p className="mt-2 font-display text-2xl font-bold tracking-tight text-secondary">{value}</p>
    </div>
  );
}

export default function Leads() {
  const [view, setView] = useState("list");
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [searchInput, setSearchInput] = useState("");
  const [sort, setSort] = useState({ by: "created_at", dir: "desc" });
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const [result, setResult] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [executives, setExecutives] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detailsId, setDetailsId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // {type:'single'|'bulk', lead?}
  const [deleting, setDeleting] = useState(false);

  // debounce search
  useEffect(() => {
    const t = setTimeout(() => { setFilters((f) => ({ ...f, search: searchInput })); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...filters, sort_by: sort.by, sort_dir: sort.dir, view };
      if (view === "list") { params.page = page; params.page_size = pageSize; }
      const { data } = await api.get("/leads", { params });
      setResult({ items: data.items, total: data.total });
    } finally {
      setLoading(false);
    }
  }, [filters, sort, page, view]);

  const fetchStats = useCallback(async () => {
    const { data } = await api.get("/leads/dashboard");
    setStats(data);
  }, []);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);
  useEffect(() => { fetchStats(); api.get("/leads/executives").then((r) => setExecutives(r.data)); }, [fetchStats]);

  const refreshAll = () => { fetchLeads(); fetchStats(); };

  const setFilter = (k, v) => { setFilters((f) => ({ ...f, [k]: v })); setPage(1); };

  const onSort = (field) => {
    setSort((s) => (s.by === field ? { by: field, dir: s.dir === "desc" ? "asc" : "desc" } : { by: field, dir: "asc" }));
    setPage(1);
  };

  const saveLead = async (payload, id) => {
    try {
      if (id) { await api.put(`/leads/${id}`, payload); toast.success("Lead updated"); }
      else { await api.post("/leads", payload); toast.success("Lead created"); }
      refreshAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save lead");
      throw err;
    }
  };

  const duplicate = async (id) => {
    await api.post(`/leads/${id}/duplicate`);
    toast.success("Lead duplicated");
    refreshAll();
  };

  const moveStatus = async (id, status) => {
    setResult((r) => ({ ...r, items: r.items.map((l) => (l.id === id ? { ...l, status } : l)) }));
    try { await api.patch(`/leads/${id}/status`, { status }); toast.success(`Moved to ${status}`); fetchStats(); }
    catch { toast.error("Failed to update status"); fetchLeads(); }
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      if (deleteTarget.type === "bulk") {
        await api.post("/leads/bulk-delete", { ids: selectedIds });
        toast.success(`${selectedIds.length} lead(s) deleted`);
        setSelectedIds([]);
      } else {
        await api.delete(`/leads/${deleteTarget.lead.id}`);
        toast.success("Lead deleted");
      }
      setDeleteTarget(null);
      refreshAll();
    } finally {
      setDeleting(false);
    }
  };

  const exportExcel = async () => {
    try {
      const res = await api.get("/leads/export", { params: filters, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sifcore_leads_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch {
      toast.error("Export failed");
    }
  };

  const toggleSelect = (id) => setSelectedIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleAll = () => {
    const ids = result.items.map((l) => l.id);
    setSelectedIds((s) => (ids.every((i) => s.includes(i)) ? s.filter((i) => !ids.includes(i)) : [...new Set([...s, ...ids])]));
  };

  const totalPages = Math.max(1, Math.ceil(result.total / pageSize));

  return (
    <div data-testid="leads-page">
      <PageHeader
        title="Lead Management"
        subtitle="Track every prospect from first contact to conversion."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={exportExcel} data-testid="export-leads-button">
              <Download className="mr-1.5 h-4 w-4" /> Export
            </Button>
            <Button onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="add-lead-button">
              <Plus className="mr-1.5 h-4 w-4" /> Add Lead
            </Button>
          </div>
        }
      />

      {/* Analytics */}
      {stats && (
        <>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <Kpi testid="stat-total-leads" icon={Users} label="Total Leads" value={stats.total_leads} tint="bg-primary/10 text-primary" />
            <Kpi testid="stat-todays-leads" icon={CalendarPlus} label="Today's Leads" value={stats.todays_leads} tint="bg-sky-500/10 text-sky-600" />
            <Kpi testid="stat-converted" icon={TrendingUp} label="Converted" value={stats.converted} tint="bg-accent/10 text-accent" />
            <Kpi testid="stat-conversion" icon={Percent} label="Conversion Rate" value={`${stats.conversion_rate}%`} tint="bg-violet-500/10 text-violet-600" />
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="rounded-xl border border-border bg-card p-5" data-testid="chart-source">
              <h3 className="mb-3 font-display text-sm font-semibold text-secondary">Leads by Source</h3>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={stats.leads_by_source} margin={{ top: 5, right: 10, left: -15, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                  <XAxis dataKey="source" tick={{ fontSize: 10, fill: "#64748B" }} interval={0} angle={-20} textAnchor="end" height={50} />
                  <YAxis tick={{ fontSize: 11, fill: "#64748B" }} allowDecimals={false} />
                  <Tooltip cursor={{ fill: "#F1F5F9" }} />
                  <Bar dataKey="count" fill="#2563EB" radius={[5, 5, 0, 0]} maxBarSize={38} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="rounded-xl border border-border bg-card p-5" data-testid="chart-monthly">
              <h3 className="mb-3 font-display text-sm font-semibold text-secondary">Monthly Leads</h3>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={stats.monthly_leads} margin={{ top: 5, right: 10, left: -15, bottom: 0 }}>
                  <defs>
                    <linearGradient id="ml" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#22C55E" stopOpacity={0.25} />
                      <stop offset="100%" stopColor="#22C55E" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#64748B" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#64748B" }} allowDecimals={false} />
                  <Tooltip />
                  <Area type="monotone" dataKey="count" stroke="#22C55E" strokeWidth={2.5} fill="url(#ml)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}

      {/* Toolbar */}
      <div className="mt-6 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-1 flex-col gap-3 sm:flex-row">
          <div className="relative flex-1 sm:max-w-xs">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search name, phone, email, ID..." className="pl-9" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} data-testid="leads-search" />
          </div>
          <Select value={filters.status} onValueChange={(v) => setFilter("status", v)}>
            <SelectTrigger className="w-full sm:w-40" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              {LEAD_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.source} onValueChange={(v) => setFilter("source", v)}>
            <SelectTrigger className="w-full sm:w-40" data-testid="filter-source"><SelectValue placeholder="Source" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sources</SelectItem>
              {LEAD_SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.assigned_to} onValueChange={(v) => setFilter("assigned_to", v)}>
            <SelectTrigger className="w-full sm:w-44" data-testid="filter-assigned"><SelectValue placeholder="Assignee" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Executives</SelectItem>
              {executives.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <Tabs value={view} onValueChange={(v) => { setView(v); setSelectedIds([]); }}>
          <TabsList data-testid="view-toggle">
            <TabsTrigger value="list" data-testid="view-list"><List className="mr-1.5 h-4 w-4" /> List</TabsTrigger>
            <TabsTrigger value="kanban" data-testid="view-kanban"><LayoutGrid className="mr-1.5 h-4 w-4" /> Kanban</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Bulk bar */}
      {view === "list" && selectedIds.length > 0 && (
        <div className="mt-4 flex items-center justify-between rounded-lg border border-primary/20 bg-primary/5 px-4 py-2.5" data-testid="bulk-bar">
          <span className="text-sm font-medium text-secondary">{selectedIds.length} selected</span>
          <Button variant="destructive" size="sm" onClick={() => setDeleteTarget({ type: "bulk" })} data-testid="bulk-delete-button">
            <Trash2 className="mr-1.5 h-4 w-4" /> Delete selected
          </Button>
        </div>
      )}

      {/* Content */}
      <div className="mt-4">
        {view === "list" ? (
          <div className="rounded-xl border border-border bg-card">
            <LeadTable
              items={result.items}
              loading={loading}
              selectedIds={selectedIds}
              onToggleSelect={toggleSelect}
              onToggleAll={toggleAll}
              onView={setDetailsId}
              onEdit={(l) => { setEditing(l); setFormOpen(true); }}
              onDuplicate={duplicate}
              onDelete={(l) => setDeleteTarget({ type: "single", lead: l })}
              sortBy={sort.by}
              sortDir={sort.dir}
              onSort={onSort}
            />
            {!loading && result.items.length > 0 && (
              <div className="flex items-center justify-between border-t border-border px-4 py-3" data-testid="pagination">
                <p className="text-sm text-muted-foreground">
                  Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, result.total)} of {result.total}
                </p>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="prev-page">Previous</Button>
                  <span className="text-sm text-slate-600">Page {page} / {totalPages}</span>
                  <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="next-page">Next</Button>
                </div>
              </div>
            )}
          </div>
        ) : loading ? (
          <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : (
          <LeadKanban items={result.items} onMove={moveStatus} onView={setDetailsId} />
        )}
      </div>

      <LeadFormDialog open={formOpen} onOpenChange={setFormOpen} lead={editing} executives={executives} onSave={saveLead} />

      <LeadDetailsSheet
        leadId={detailsId}
        open={Boolean(detailsId)}
        onOpenChange={(v) => { if (!v) setDetailsId(null); }}
        executives={executives}
        onChanged={refreshAll}
      />

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(v) => { if (!v) setDeleteTarget(null); }}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteTarget?.type === "bulk" ? `${selectedIds.length} leads` : "this lead"}?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. All related follow-ups and activity history will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} disabled={deleting} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete">
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
